import React, { memo, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import L from "leaflet";
import {
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import useAuth from "../../../hooks/useAuth";
import {
  generatePermitRouteMap,
  PermitRouteCoordinateDto,
  PermitRouteMapDto,
} from "../../../api/permitRouteMapService";

interface PermitRouteMapProps {
  routeText?: string | null;
  defaultState?: string | null;
  existingMapData?: PermitRouteMapDto | null;
  height?: number | string;
  onMapDataGenerated?: (mapData: PermitRouteMapDto) => void;
  autoGenerate?: boolean;
  showShareLink?: boolean;
  sharePath?: string;
  readOnly?: boolean;
  minimal?: boolean;
}

type LatLngTuple = [number, number];

interface OsrmRouteResponse {
  code?: string;
  routes?: Array<{
    geometry?: {
      coordinates?: Array<[number, number]>;
    };
  }>;
}

const routeMapStorageKey = (
  routeText?: string | null,
  defaultState?: string | null,
) => `permit-route-map:${defaultState ?? ""}:${routeText ?? ""}`;

const isValidCoordinate = (point?: PermitRouteCoordinateDto | null) =>
  point != null &&
  Number.isFinite(point.latitude) &&
  Number.isFinite(point.longitude);

const pointColor = (point: PermitRouteCoordinateDto) => {
  if (point.pointType === "border-start") return "#2e7d32";
  if (point.pointType === "border-end") return "#c62828";
  if (point.pointType === "review" || point.generatedFallback) return "#ed6c02";
  return "#1976d2";
};

const coordinateToLatLng = (point: PermitRouteCoordinateDto): LatLngTuple => [
  point.latitude,
  point.longitude,
];

const buildFallbackPolyline = (
  points: PermitRouteCoordinateDto[],
): LatLngTuple[] => points.filter(isValidCoordinate).map(coordinateToLatLng);

const fetchRoadSnappedPolyline = async (
  points: PermitRouteCoordinateDto[],
  signal?: AbortSignal,
): Promise<LatLngTuple[]> => {
  const validPoints = points.filter(isValidCoordinate);
  if (validPoints.length < 2) return buildFallbackPolyline(validPoints);

  const coordinates = validPoints
    .map((point) => `${point.longitude},${point.latitude}`)
    .join(";");
  const response = await fetch(
    `https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=full&geometries=geojson&steps=false&continue_straight=true`,
    { signal },
  );

  if (!response.ok) {
    throw new Error("Road-snapped route request failed.");
  }

  const data = (await response.json()) as OsrmRouteResponse;
  const geometry = data.routes?.[0]?.geometry?.coordinates;
  if (data.code !== "Ok" || !geometry?.length) {
    throw new Error("Road-snapped route geometry was not returned.");
  }

  return geometry.map(([longitude, latitude]) => [latitude, longitude]);
};

const formatCoordinate = (value?: number | null) =>
  Number.isFinite(value) ? Number(value).toFixed(6) : "";

const escapePromptCsvValue = (value: unknown) =>
  `"${String(value ?? "").replace(/"/g, '""')}"`;

const buildLatestCoordinatePrompt = (mapData?: PermitRouteMapDto | null) => {
  if (!mapData?.steps.length) return "";

  const rows = [
    "Order,Instruction,Latitude,Longitude",
    ...mapData.steps.map((step) =>
      [
        step.order,
        escapePromptCsvValue(step.instruction ?? step.coordinate?.label ?? ""),
        formatCoordinate(step.coordinate?.latitude),
        formatCoordinate(step.coordinate?.longitude),
      ].join(","),
    ),
  ];

  return rows.join("\n");
};
const encodeSharePayload = (value: unknown) => {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
};

const routeStepIcon = (point: PermitRouteCoordinateDto) =>
  L.divIcon({
    className: "permit-route-step-marker",
    html: `<div style="background:${pointColor(
      point,
    )};color:white;border:2px solid white;border-radius:999px;box-shadow:0 1px 5px rgba(0,0,0,.45);height:26px;min-width:26px;padding:0 5px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;">${point.stepOrder}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15],
  });

function FitRouteBounds({ points }: { points: PermitRouteCoordinateDto[] }) {
  const map = useMap();

  useEffect(() => {
    const validPoints = points.filter(isValidCoordinate);
    if (!validPoints.length) return;

    const bounds = L.latLngBounds(validPoints.map(coordinateToLatLng));
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
    }
  }, [map, points]);

  return null;
}

const RouteMarkers = memo(
  ({
    points,
    onCoordinateChange,
    readOnly = false,
  }: {
    points: PermitRouteCoordinateDto[];
    onCoordinateChange: (
      stepOrder: number,
      latitude: number,
      longitude: number,
    ) => void;
    readOnly?: boolean;
  }) => (
    <>
      {points.filter(isValidCoordinate).map((point) => (
        <Marker
          key={`${point.stepOrder}-${point.latitude}-${point.longitude}`}
          position={[point.latitude, point.longitude]}
          draggable={!readOnly}
          icon={routeStepIcon(point)}
          eventHandlers={
            readOnly
              ? undefined
              : {
                  dragend: (event) => {
                    const marker = event.target as L.Marker;
                    const nextPosition = marker.getLatLng();
                    onCoordinateChange(
                      point.stepOrder,
                      nextPosition.lat,
                      nextPosition.lng,
                    );
                  },
                }
          }
        >
          <Tooltip direction="top" offset={[0, -14]} opacity={0.95}>
            {point.stepOrder}. {point.label ?? "Route step"}
          </Tooltip>
          <Popup>
            <Typography variant="subtitle2">Step {point.stepOrder}</Typography>
            <Typography variant="body2">
              {point.label ?? "Route step"}
            </Typography>
            {!readOnly && (
              <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                Drag this marker to correct the coordinate. The route and latest
                coordinate prompt update automatically.
              </Typography>
            )}
            {point.generatedFallback && (
              <Chip
                size="small"
                color="warning"
                label="Needs review"
                sx={{ mt: 1 }}
              />
            )}
          </Popup>
        </Marker>
      ))}
    </>
  ),
);

const PermitRouteMap: React.FC<PermitRouteMapProps> = ({
  routeText,
  defaultState,
  existingMapData,
  height = 460,
  onMapDataGenerated,
  autoGenerate = true,
  showShareLink = true,
  sharePath = "/permit-route-map",
  readOnly = false,
  minimal = false,
}) => {
  const { token } = useAuth();
  const [mapData, setMapData] = useState<PermitRouteMapDto | null>(
    existingMapData ?? null,
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSnappingRoute, setIsSnappingRoute] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [routeSnapWarning, setRouteSnapWarning] = useState<string | null>(null);
  const [roadPolylinePositions, setRoadPolylinePositions] = useState<
    LatLngTuple[]
  >([]);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const storageKey = useMemo(
    () => routeMapStorageKey(routeText, defaultState),
    [routeText, defaultState],
  );
  const fallbackPolylinePositions = useMemo(
    () => buildFallbackPolyline(mapData?.polyline ?? mapData?.points ?? []),
    [mapData],
  );
  const displayedPolylinePositions =
    roadPolylinePositions.length > 1
      ? roadPolylinePositions
      : fallbackPolylinePositions;
  const reviewSteps = useMemo(
    () => mapData?.steps.filter((step) => step.requiresReview) ?? [],
    [mapData],
  );
  const latestCoordinatePrompt = useMemo(
    () => buildLatestCoordinatePrompt(mapData),
    [mapData],
  );

  const generateMap = async (forceRefresh = false) => {
    if (!routeText?.trim()) {
      setMapData(null);
      setError(null);
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const cached = !forceRefresh ? localStorage.getItem(storageKey) : null;
      const cachedMap = cached
        ? (JSON.parse(cached) as PermitRouteMapDto)
        : null;
      const generated = await generatePermitRouteMap(
        {
          routeText,
          defaultState,
          forceRefresh,
          existingMapData: existingMapData ?? cachedMap,
        },
        token,
      );

      setMapData(generated);
      localStorage.setItem(storageKey, JSON.stringify(generated));
      onMapDataGenerated?.(generated);
    } catch (err: any) {
      setError(
        err?.response?.data || "Failed to generate permit route map data.",
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCoordinateChange = (
    stepOrder: number,
    latitude: number,
    longitude: number,
  ) => {
    if (readOnly) return;

    setMapData((current) => {
      if (!current) return current;

      const updatedPoints = current.points.map((point) =>
        point.stepOrder === stepOrder
          ? {
              ...point,
              latitude,
              longitude,
              generatedFallback: false,
              pointType:
                point.pointType === "review" ? "route-step" : point.pointType,
            }
          : point,
      );
      const updatedPolyline = current.polyline.map((point) =>
        point.stepOrder === stepOrder
          ? {
              ...point,
              latitude,
              longitude,
              generatedFallback: false,
              pointType:
                point.pointType === "review" ? "route-step" : point.pointType,
            }
          : point,
      );
      const updatedSteps = current.steps.map((step) =>
        step.order === stepOrder
          ? {
              ...step,
              requiresReview: false,
              reviewReason: null,
              coordinate: step.coordinate
                ? {
                    ...step.coordinate,
                    latitude,
                    longitude,
                    generatedFallback: false,
                    pointType:
                      step.coordinate.pointType === "review"
                        ? "route-step"
                        : step.coordinate.pointType,
                  }
                : {
                    latitude,
                    longitude,
                    stepOrder,
                    label: step.roadName ?? step.instruction,
                    pointType: "route-step",
                    generatedFallback: false,
                  },
            }
          : step,
      );

      const updatedMap = {
        ...current,
        steps: updatedSteps,
        points: updatedPoints,
        polyline: updatedPolyline,
        startBorderPoint:
          current.startBorderPoint?.stepOrder === stepOrder
            ? (updatedPoints.find((point) => point.stepOrder === stepOrder) ??
              current.startBorderPoint)
            : current.startBorderPoint,
        endBorderPoint:
          current.endBorderPoint?.stepOrder === stepOrder
            ? (updatedPoints.find((point) => point.stepOrder === stepOrder) ??
              current.endBorderPoint)
            : current.endBorderPoint,
        hasFailedSteps: updatedSteps.some((step) => step.requiresReview),
      };

      localStorage.setItem(storageKey, JSON.stringify(updatedMap));
      onMapDataGenerated?.(updatedMap);
      return updatedMap;
    });
  };

  const copyLatestCoordinatePrompt = async () => {
    if (!latestCoordinatePrompt) return;

    await navigator.clipboard.writeText(latestCoordinatePrompt);
    setCopyMessage("Latest coordinate prompt copied.");
    window.setTimeout(() => setCopyMessage(null), 2500);
  };

  const copyShareLink = async () => {
    if (!mapData) return;

    const shareUrl = `${window.location.origin}${sharePath}#map=${encodeSharePayload(mapData)}`;
    await navigator.clipboard.writeText(shareUrl);
    setCopyMessage("Public share link copied.");
    window.setTimeout(() => setCopyMessage(null), 2500);
  };

  useEffect(() => {
    if (!autoGenerate) return;

    generateMap(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeText, defaultState, autoGenerate]);

  useEffect(() => {
    const points = mapData?.points ?? [];
    setRoadPolylinePositions([]);
    setRouteSnapWarning(null);

    if (points.filter(isValidCoordinate).length < 2) {
      return;
    }

    const abortController = new AbortController();
    setIsSnappingRoute(true);

    fetchRoadSnappedPolyline(points, abortController.signal)
      .then((polyline) => {
        setRoadPolylinePositions(polyline);
      })
      .catch(() => {
        if (!abortController.signal.aborted) {
          setRouteSnapWarning(
            "Road-following route could not be generated, so the map is showing the direct permit point polyline.",
          );
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsSnappingRoute(false);
        }
      });

    return () => abortController.abort();
  }, [mapData]);

  if (!routeText?.trim() && !mapData?.points.length) {
    return (
      <Alert severity="info">
        Permit route text is required to show the route map.
      </Alert>
    );
  }

  return (
    <Stack spacing={minimal ? 0 : 2}>
      {!minimal && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`${mapData?.points.length ?? 0} route points`} />
            {displayedPolylinePositions.length > 1 && (
              <Chip
                label={`${displayedPolylinePositions.length} polyline vertices`}
              />
            )}
            {mapData?.totalDistanceMiles != null && (
              <Chip label={`${mapData.totalDistanceMiles} total miles`} />
            )}
            {mapData?.stateMileageMiles != null && (
              <Chip label={`${mapData.stateMileageMiles} state miles`} />
            )}
            {mapData?.startBorderPoint && (
              <Chip color="success" label="Start border found" />
            )}
            {mapData?.endBorderPoint && (
              <Chip color="error" label="End border found" />
            )}
            {mapData?.hasFailedSteps && (
              <Chip
                color="warning"
                label={`${reviewSteps.length} step(s) need review`}
              />
            )}
            {roadPolylinePositions.length > 1 && (
              <Chip color="primary" label="Road-following route" />
            )}
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={copyLatestCoordinatePrompt}
              disabled={!latestCoordinatePrompt}
            >
              Copy latest prompt
            </Button>
            {showShareLink && (
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={copyShareLink}
                disabled={!mapData?.points.length}
              >
                Copy public link
              </Button>
            )}
            {autoGenerate && (
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => generateMap(true)}
                disabled={isGenerating}
              >
                Refresh route
              </Button>
            )}
          </Stack>
        </Box>
      )}

      {!minimal && error && <Alert severity="error">{error}</Alert>}
      {!minimal && isGenerating && (
        <Alert icon={<CircularProgress size={18} />} severity="info">
          Processing permit route instructions and generating map coordinates...
        </Alert>
      )}
      {!minimal && isSnappingRoute && (
        <Alert icon={<CircularProgress size={18} />} severity="info">
          Generating one road-following route through every permit point in
          order...
        </Alert>
      )}
      {!minimal && reviewSteps.length > 0 && (
        <Alert severity="warning">
          Some route steps were estimated and should be reviewed:{" "}
          {reviewSteps.map((step) => step.order).join(", ")}.
        </Alert>
      )}
      {!minimal && routeSnapWarning && (
        <Alert severity="warning">{routeSnapWarning}</Alert>
      )}
      {!minimal && roadPolylinePositions.length > 1 && (
        <Alert severity="success">
          The route is drawn as one continuous road-following polyline from the
          first permit point to the last permit point.
        </Alert>
      )}
      {!minimal && copyMessage && (
        <Alert severity="success">{copyMessage}</Alert>
      )}
      {!minimal && latestCoordinatePrompt && (
        <TextField
          label="Latest coordinate prompt"
          value={latestCoordinatePrompt}
          multiline
          minRows={4}
          maxRows={8}
          fullWidth
          InputProps={{ readOnly: true }}
          helperText="Drag incorrect map markers to update coordinates; this prompt refreshes automatically."
        />
      )}

      <Box
        sx={{
          height,
          width: "100%",
          borderRadius: minimal ? 0 : 1,
          overflow: "hidden",
          border: minimal ? "none" : "1px solid",
          borderColor: "divider",
        }}
      >
        <MapContainer
          center={[40.6331, -89.3985]}
          zoom={7}
          style={{ height: "100%", width: "100%" }}
          preferCanvas
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {displayedPolylinePositions.length > 1 && (
            <Polyline
              positions={displayedPolylinePositions}
              pathOptions={{ color: "#1565c0", weight: 4, opacity: 0.85 }}
            />
          )}
          {fallbackPolylinePositions.length > 1 &&
            roadPolylinePositions.length > 1 && (
              <Polyline
                positions={fallbackPolylinePositions}
                pathOptions={{
                  color: "#90caf9",
                  weight: 2,
                  opacity: 0.45,
                  dashArray: "6 8",
                }}
              />
            )}
          {mapData?.points && (
            <RouteMarkers
              points={mapData.points}
              onCoordinateChange={handleCoordinateChange}
              readOnly={readOnly}
            />
          )}
          <FitRouteBounds points={mapData?.points ?? []} />
        </MapContainer>
      </Box>
    </Stack>
  );
};

export default PermitRouteMap;
